import type { ReactNode } from 'react';
import { T, Var } from 'gt-react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  EatonDspFileResult,
  EatonDspSummary,
  EatonDuplicateRow,
  EatonMissingSpa,
} from '@/lib/eaton-dsp/types';
import { SummaryStat } from '../SummaryStat';

function FileStatusBadge({ result }: { result: EatonDspFileResult }) {
  if (result.status === 'extracted') {
    return (
      <Badge variant="success">
        <T context="badge when an Eaton DSP workbook was extracted">
          Extracted
        </T>
      </Badge>
    );
  }
  if (result.status === 'unreadable') {
    return (
      <Badge variant="destructive">
        <T context="badge when an Eaton DSP file could not be parsed">
          Couldn’t read
        </T>
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <T context="badge when a dropped file is not an Eaton DSP workbook">
        Unrecognized
      </T>
    </Badge>
  );
}

function FileRow({
  fileName,
  badge,
  onRemove,
  isExtracting,
}: {
  fileName: string;
  badge: ReactNode;
  onRemove: () => void;
  isExtracting: boolean;
}) {
  return (
    <li className="flex items-center gap-2 rounded-lg border p-3">
      <span className="min-w-0 flex-1 truncate text-sm font-medium">
        {fileName}
      </span>
      {badge}
      <button
        type="button"
        className="text-xs text-muted-foreground underline-offset-2 hover:underline disabled:no-underline disabled:opacity-50"
        disabled={isExtracting}
        onClick={onRemove}
      >
        <T context="button that removes one Eaton DSP file from the list">
          Remove
        </T>
      </button>
    </li>
  );
}

export function FileResultList({
  results,
  onRemove,
  isExtracting = false,
}: {
  results: EatonDspFileResult[];
  onRemove: (fileName: string) => void;
  isExtracting?: boolean;
}) {
  if (results.length === 0) return null;
  return (
    <ul className="flex flex-col gap-2">
      {results.map((result) => (
        <FileRow
          key={result.fileName}
          fileName={result.fileName}
          badge={<FileStatusBadge result={result} />}
          isExtracting={isExtracting}
          onRemove={() => onRemove(result.fileName)}
        />
      ))}
    </ul>
  );
}

export function ExtractSummary({ summary }: { summary: EatonDspSummary }) {
  if (summary.fileCount === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <SummaryStat
        label={<T context="count of Eaton DSP files dropped">Files</T>}
        value={summary.fileCount}
      />
      <SummaryStat
        label={<T context="count of distinct Eaton SPA ids extracted">SPAs</T>}
        value={summary.spaCount}
      />
      <SummaryStat
        label={
          <T context="count of flattened Eaton catalog price rows">Prices</T>
        }
        value={summary.priceCount}
      />
      <SummaryStat
        label={
          <T context="count of Eaton catalogs that appear on more than one SPA or price">
            Duplicates
          </T>
        }
        value={summary.duplicateCount}
      />
    </div>
  );
}

const PREVIEW_LIMIT = 50;

function PreviewTable({
  title,
  caption,
  headers,
  rows,
}: {
  title: ReactNode;
  caption?: ReactNode;
  headers: Array<{ key: string; node: ReactNode }>;
  rows: Array<{ key: string; cells: Array<{ key: string; node: ReactNode }> }>;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      {caption}
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              {headers.map((header) => (
                <TableHead key={header.key}>{header.node}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                {row.cells.map((cell) => (
                  <TableCell key={cell.key}>{cell.node}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function DuplicatesTable({ rows }: { rows: EatonDuplicateRow[] }) {
  const visible = rows.slice(0, PREVIEW_LIMIT);
  return (
    <PreviewTable
      title={
        <T context="heading for Eaton catalogs that collide across SPAs or prices">
          Duplicate catalogs
        </T>
      }
      caption={
        rows.length > PREVIEW_LIMIT ? (
          <p className="text-xs text-muted-foreground">
            <T context="note that the on-screen duplicate table is a preview">
              Showing <Var>{PREVIEW_LIMIT}</Var> of <Var>{rows.length}</Var>{' '}
              duplicates. Download the extract for the full list.
            </T>
          </p>
        ) : null
      }
      headers={[
        {
          key: 'catalog',
          node: (
            <T context="column header for an Eaton catalog number">Catalogue</T>
          ),
        },
        {
          key: 'spas',
          node: (
            <T context="column header listing SPA ids that share a catalog">
              SPAs
            </T>
          ),
        },
        {
          key: 'prices',
          node: (
            <T context="column header listing distinct net prices for a catalog">
              Prices
            </T>
          ),
        },
      ]}
      rows={visible.map((row) => ({
        key: row.catalog,
        cells: [
          {
            key: 'catalog',
            node: <span className="font-medium">{row.catalog}</span>,
          },
          {
            key: 'spas',
            node: (
              <span className="max-w-[20rem] truncate text-xs">
                {row.spaIds.join(', ')}
              </span>
            ),
          },
          {
            key: 'prices',
            node: <span className="text-xs">{row.prices.join(', ')}</span>,
          },
        ],
      }))}
    />
  );
}

export function MissingSpasTable({ rows }: { rows: EatonMissingSpa[] }) {
  return (
    <PreviewTable
      title={
        <T context="heading for master-list SPAs that have no price tab">
          Master SPAs without a price tab
        </T>
      }
      headers={[
        {
          key: 'spa',
          node: <T context="column header for a missing Eaton SPA id">SPA</T>,
        },
        {
          key: 'description',
          node: (
            <T context="column header for a missing Eaton SPA description">
              Description
            </T>
          ),
        },
        {
          key: 'regions',
          node: (
            <T context="column header for regions on a missing Eaton SPA">
              Regions
            </T>
          ),
        },
      ]}
      rows={rows.map((row) => ({
        key: row.spaId,
        cells: [
          {
            key: 'spa',
            node: <span className="font-medium">{row.spaId}</span>,
          },
          {
            key: 'description',
            node: (
              <span className="max-w-[20rem] truncate">
                {row.spaDescription}
              </span>
            ),
          },
          {
            key: 'regions',
            node: <span className="text-xs">{row.regions}</span>,
          },
        ],
      }))}
    />
  );
}
